import { dayKey } from '@qr/shared/paths';
import { isUnlimited } from '@qr/shared/plans';
import { shortLinkOf } from '@qr/shared/slug';
import { fileUrl } from '@qr/shared/storage';
import type { QrCode, QrDesign, QrType } from '@qr/shared/types';
import { Button, Callout, Field, Spinner } from '@qr/ui';
import {
  collection,
  collectionGroup,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Cajon } from '../components/Cajon.js';
import { GestorDeCarpetas, useCarpetas } from '../components/Carpetas.js';
import { CrearEnLote } from '../components/CrearEnLote.js';
import { Chispa } from '../components/Chispa.js';
import {
  HOSTED_TYPES,
  PayloadForm,
  payloadInicial,
  payloadListo,
  type PayloadValue,
} from '../components/PayloadForms.js';
import { descargarQr, QrPreview } from '../components/QrPreview.js';
import { api, messageFromError } from '../lib/api.js';
import { R2_PUBLIC_BASE, SHORT_LINK_BASE, db } from '../lib/firebase.js';
import { useCan, useOrg } from '../lib/org.js';

/**
 * Direccion del logo de un codigo.
 *
 * La ruta manda sobre la direccion guardada. `logoUrl` solo lo tienen los
 * codigos creados antes de guardarse la ruta, y se sigue leyendo para que no
 * se queden sin logo.
 */
function logoDe(design: QrDesign | undefined): string {
  return fileUrl(design?.logoKey ?? design?.logoUrl, R2_PUBLIC_BASE);
}

interface CodeRow extends QrCode {
  readonly id: string;
}

/** Días que cubre la chispa de cada ficha. */
const DIAS_CHISPA = 7;

const ETIQUETAS_TIPO: Record<QrType, string> = {
  url: 'Enlace',
  vcard: 'Tarjeta de contacto',
  wifi: 'Red wifi',
  links: 'Lista de enlaces',
  file: 'Archivo',
  text: 'Texto',
};

/**
 * La rejilla de fichas crece con la pantalla.
 *
 * Un minimo fijo deja la interfaz dimensionada para un portatil: en un monitor
 * de 2560 las fichas se ven diminutas y sobra sitio por todas partes. Cada
 * escalon sube el minimo, asi que el navegador sigue decidiendo cuantas
 * columnas caben pero partiendo de un tamano razonable para esa pantalla.
 */
const REJILLA = [
  'grid gap-4',
  'grid-cols-[repeat(auto-fill,minmax(280px,1fr))]',
  'xl:grid-cols-[repeat(auto-fill,minmax(320px,1fr))]',
  '2xl:grid-cols-[repeat(auto-fill,minmax(360px,1fr))]',
  '3xl:grid-cols-[repeat(auto-fill,minmax(410px,1fr))] 3xl:gap-5',
].join(' ');

/**
 * Tamanos de la ficha por tramos de pantalla.
 *
 * No basta con ensanchar la tarjeta: si el QR, los textos y las cifras se
 * quedan clavados, lo unico que crece es el hueco vacio de dentro y la ficha
 * se ve peor, no mejor. Aqui crece TODO a la vez, en la misma proporcion.
 *
 * Viven juntos y no repartidos por el JSX para que sea evidente de un vistazo
 * que forman una escala: tocar uno sin mirar los demas la rompe.
 */
const FICHA =
  'flex flex-col gap-3 rounded-lg border border-line bg-surface p-3.5 xl:gap-3.5 xl:p-4 2xl:gap-4 2xl:p-5 3xl:p-6';

const FICHA_QR =
  'qr-fluido size-[56px] shrink-0 rounded-sm bg-white p-1 xl:size-[68px] 2xl:size-[84px] 2xl:p-1.5 3xl:size-[104px]';

const FICHA_TITULO =
  'block truncate font-semibold text-ink no-underline hover:text-brand text-[14.5px] xl:text-base 2xl:text-[17.5px] 3xl:text-xl';

const FICHA_ETIQUETA =
  'mt-1 inline-block rounded-full bg-brand-soft px-2 py-0.5 font-medium text-brand-deep text-[10.5px] xl:text-[11.5px] 2xl:text-[12.5px] 2xl:px-2.5';

const FICHA_ENLACE =
  'flex items-center gap-2 rounded-sm border border-line bg-ground px-2.5 py-2 text-ink-2 text-[11.5px] xl:text-[12.5px] 2xl:text-[13.5px] 2xl:px-3 2xl:py-2.5';

const FICHA_CIFRA =
  'block font-bold leading-tight tracking-tight tabular-nums text-[23px] xl:text-[27px] 2xl:text-[32px] 3xl:text-[38px]';

const FICHA_PIE = 'text-ink-3 text-[11px] xl:text-[12px] 2xl:text-[13px]';

const FICHA_CHISPA = 'w-[80px] h-auto xl:w-[96px] 2xl:w-[116px] 3xl:w-[136px]';

const BOTON_FICHA =
  'flex-1 rounded-sm border border-line py-1.5 text-center font-medium text-ink-2 transition-colors hover:text-ink disabled:opacity-60 text-[11.5px] xl:text-[12.5px] xl:py-2 2xl:text-[13.5px] 2xl:py-2.5';

type Filtro = 'todos' | 'activos' | 'pausados' | 'url' | 'file';
type Orden = 'recientes' | 'escaneados' | 'nombre';

/**
 * Listado de códigos.
 *
 * La lista manda y crear es un cajón que se abre. Antes era al revés —un
 * formulario vacío clavado en la mitad superior— y eso hacía que lo primero
 * que vieras al entrar fuese trabajo por hacer en vez de lo que ya tienes.
 */
export function Codes() {
  const org = useOrg();
  const canEdit = useCan('editor');

  const [codes, setCodes] = useState<CodeRow[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [series, setSeries] = useState<Record<string, number[]>>({});

  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [orden, setOrden] = useState<Orden>('recientes');
  const [creando, setCreando] = useState(false);
  const [modo, setModo] = useState<'uno' | 'lote'>('uno');
  const [carpeta, setCarpeta] = useState<string | null>(null);
  const [gestionando, setGestionando] = useState(false);
  const carpetas = useCarpetas(org.orgId);

  useEffect(() => {
    if (!org.orgId) return;

    // El filtro por orgId no es solo para mostrar lo correcto: las reglas de
    // seguridad rechazan la consulta entera si falta.
    const q = query(
      collection(db, 'qrcodes'),
      where('orgId', '==', org.orgId),
      orderBy('createdAt', 'desc'),
      limit(100),
    );

    return onSnapshot(
      q,
      (snap) => {
        setCodes(snap.docs.map((d) => ({ id: d.id, ...(d.data() as QrCode) })));
        setListError(null);
      },
      (error) => {
        console.error('No se pudieron cargar los codigos', error);
        setListError(messageFromError(error));
        setCodes([]);
      },
    );
  }, [org.orgId]);

  /**
   * Los últimos días de todos los códigos, en UNA consulta.
   *
   * Los agregados diarios llevan el orgId denormalizado y hay un índice de
   * grupo sobre (orgId, day), así que no hace falta una consulta por código:
   * con cien códigos eso serían cien lecturas cada vez que entras a la lista.
   */
  useEffect(() => {
    if (!org.orgId) return;

    const desde = new Date();
    desde.setUTCDate(desde.getUTCDate() - (DIAS_CHISPA - 1));

    const q = query(
      collectionGroup(db, 'daily'),
      where('orgId', '==', org.orgId),
      where('day', '>=', dayKey(desde)),
    );

    return onSnapshot(
      q,
      (snap) => {
        const dias = diasRecientes();
        const porCodigo: Record<string, number[]> = {};

        for (const d of snap.docs) {
          const codeId = d.ref.parent.parent?.id;
          if (!codeId) continue;
          const indice = dias.indexOf(d.get('day') as string);
          if (indice < 0) continue;
          porCodigo[codeId] ??= Array.from({ length: DIAS_CHISPA }, () => 0);
          porCodigo[codeId][indice] = (d.get('scans') as number | undefined) ?? 0;
        }

        setSeries(porCodigo);
      },
      (error) => {
        // Sin chispas la lista sigue siendo perfectamente usable, así que esto
        // no se le cuenta a nadie; queda en la consola por si hay que mirarlo.
        console.error('No se pudieron cargar las series de escaneos', error);
      },
    );
  }, [org.orgId]);

  const atCodeLimit = useMemo(() => {
    if (!codes || isUnlimited(org.limits.codes)) return false;
    return codes.length >= org.limits.codes;
  }, [codes, org.limits.codes]);

  const visibles = useMemo(() => {
    if (!codes) return null;
    const texto = busqueda.trim().toLowerCase();

    const filtrados = codes.filter((code) => {
      if (filtro === 'activos' && !code.active) return false;
      if (filtro === 'pausados' && code.active) return false;
      if (carpeta !== null && (code.folderId ?? '') !== carpeta) return false;
      if (filtro === 'url' && code.type !== 'url') return false;
      if (filtro === 'file' && code.type !== 'file') return false;
      if (!texto) return true;
      return (
        code.name.toLowerCase().includes(texto) ||
        code.slug.toLowerCase().includes(texto) ||
        (code.target.kind === 'url' && code.target.url.toLowerCase().includes(texto))
      );
    });

    // `toSorted` y no `sort`: `codes` es el estado, y ordenarlo en el sitio lo
    // mutaría por debajo de React.
    if (orden === 'escaneados') {
      return filtrados.toSorted((a, b) => b.scanCount - a.scanCount);
    }
    if (orden === 'nombre') {
      return filtrados.toSorted((a, b) => a.name.localeCompare(b.name, 'es'));
    }
    return filtrados;
  }, [codes, busqueda, filtro, orden, carpeta]);

  const hayCodigos = (codes?.length ?? 0) > 0;

  return (
    // Sin ancho maximo ni centrado: el contenido llena lo que hay. Centrarlo
    // dejaba dos franjas negras muertas a los lados en cuanto la pantalla
    // pasaba de 1500 px, que es justo donde se trabaja.
    <div className="flex flex-col">
      {/* La cabecera y los filtros son bandas de borde a borde, cada una con
          su linea: es lo que separa "mandos" de "contenido" de un vistazo. */}
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 lg:px-6">
        <h1 className="m-0 text-lg font-semibold tracking-tight">Códigos</h1>
        {codes ? (
          <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3 tabular-nums">
            {codes.length}
          </span>
        ) : null}

        <div className="ms-auto flex flex-wrap items-center gap-2.5">
          {hayCodigos ? (
            <label className="flex items-center gap-2 rounded-sm border border-line bg-surface px-3 py-1.5 text-ink-3 focus-within:border-brand">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                id="buscar-codigos"
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o enlace"
                className="w-40 border-0 bg-transparent p-0 text-[13.5px] text-ink outline-none placeholder:text-ink-3 sm:w-56"
              />
              <span className="sr-only">Buscar códigos</span>
            </label>
          ) : null}

          {canEdit ? (
            <Button onClick={() => setCreando(true)} disabled={atCodeLimit}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 3v10M3 8h10"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              </svg>
              Crear código
            </Button>
          ) : null}
        </div>
      </header>

      {hayCodigos ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 lg:px-6">
          <Chip activo={filtro === 'todos'} onClick={() => setFiltro('todos')}>
            Todos
          </Chip>
          <Chip activo={filtro === 'activos'} onClick={() => setFiltro('activos')}>
            Activos
          </Chip>
          <Chip activo={filtro === 'pausados'} onClick={() => setFiltro('pausados')}>
            Pausados
          </Chip>
          <Chip activo={filtro === 'url'} onClick={() => setFiltro('url')}>
            Enlace
          </Chip>
          <Chip activo={filtro === 'file'} onClick={() => setFiltro('file')}>
            Archivo
          </Chip>

          {/* El orden va del lado de los filtros, con la misma forma de
              pildora: es otro mando de la lista, no un campo de formulario. */}
          <label className="relative ms-auto inline-flex items-center gap-1.5 rounded-full border border-line bg-surface ps-3 pe-7 py-1.5 text-[12.5px] text-ink-2">
            <span className="sr-only">Ordenar por</span>
            <select
              id="orden-codigos"
              value={orden}
              onChange={(e) => setOrden(e.target.value as Orden)}
              className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-[12.5px] text-ink-2 outline-none"
            >
              <option value="recientes">Más recientes</option>
              <option value="escaneados">Más escaneados</option>
              <option value="nombre">Nombre</option>
            </select>
            <svg
              width="10"
              height="10"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="pointer-events-none absolute end-2.5"
            >
              <path
                d="M4 6.5L8 10.5l4-4"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </label>
        </div>
      ) : null}

      {hayCodigos && canEdit ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 lg:px-6">
          <span className="text-[12px] text-ink-3">Carpetas</span>
          <Chip activo={carpeta === null} onClick={() => setCarpeta(null)}>
            Todas
          </Chip>
          {carpetas.map((c) => (
            <Chip key={c.id} activo={carpeta === c.id} onClick={() => setCarpeta(c.id)}>
              {c.name}
            </Chip>
          ))}
          <Chip activo={carpeta === ''} onClick={() => setCarpeta('')}>
            Sin carpeta
          </Chip>
          <button
            type="button"
            onClick={() => setGestionando(true)}
            className="ms-auto rounded-sm px-2 py-1 text-[12.5px] text-ink-2 underline-offset-2 hover:text-ink hover:underline"
          >
            Gestionar carpetas
          </button>
        </div>
      ) : null}

      <div className="flex flex-col gap-5 px-4 py-5 lg:px-6">
        {atCodeLimit && !org.isComped ? (
          <Callout tone="warning" title="Has alcanzado el límite de códigos">
            Tu plan incluye {org.limits.codes}{' '}
            {org.limits.codes === 1 ? 'código' : 'códigos'}. Elimina alguno que ya no
            uses o cambia de plan para crear más.
          </Callout>
        ) : null}

        {listError ? <Callout tone="danger">{listError}</Callout> : null}

        {codes === null ? (
          <div className="center">
            <Spinner label="Cargando códigos" />
          </div>
        ) : codes.length === 0 ? (
          <Vacio puedeCrear={canEdit} onCrear={() => setCreando(true)} />
        ) : visibles && visibles.length === 0 ? (
          <p className="rounded border border-dashed border-line px-4 py-10 text-center text-ink-2">
            Ningún código coincide con lo que buscas.
          </p>
        ) : (
          <div className={REJILLA}>
            {visibles?.map((code) => (
              <Ficha key={code.id} code={code} dias={series[code.id]} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {gestionando ? (
          <Cajon titulo="Carpetas" onCerrar={() => setGestionando(false)}>
            <GestorDeCarpetas
              orgId={org.orgId}
              carpetas={carpetas}
              puedeBorrar={org.role === 'owner' || org.role === 'admin'}
            />
          </Cajon>
        ) : null}
        {creando ? (
          <Cajon titulo="Crear códigos" onCerrar={() => setCreando(false)}>
            {/* El lote solo aparece si el plan lo incluye. Ensenar una pestana
                que al pulsarla dice "no puedes" es peor que no ensenarla. */}
            {org.limits.bulkCreate ? (
              <div className="mb-5 flex gap-1 rounded-sm border border-line p-1">
                {(['uno', 'lote'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={modo === m}
                    onClick={() => setModo(m)}
                    className={[
                      'flex-1 rounded-sm py-1.5 text-[13px] transition-colors',
                      modo === m
                        ? 'bg-brand-soft font-semibold text-brand-deep'
                        : 'text-ink-2 hover:text-ink',
                    ].join(' ')}
                  >
                    {m === 'uno' ? 'Uno' : 'En lote'}
                  </button>
                ))}
              </div>
            ) : null}

            {modo === 'lote' && org.limits.bulkCreate ? (
              <CrearEnLote orgId={org.orgId} onHecho={() => setCreando(false)} />
            ) : (
              <NewCodeForm orgId={org.orgId} onHecho={() => setCreando(false)} />
            )}
          </Cajon>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function Chip({
  activo,
  onClick,
  children,
}: {
  readonly activo: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={[
        'rounded-full border px-3 py-1.5 text-[12.5px] transition-colors',
        activo
          ? 'border-brand/35 bg-brand-soft font-semibold text-brand-deep'
          : 'border-line bg-surface text-ink-2 hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Ficha({
  code,
  dias,
}: {
  readonly code: CodeRow;
  readonly dias: number[] | undefined;
}) {
  const shortLink = shortLinkOf(code, SHORT_LINK_BASE);
  const destino =
    code.target.kind === 'url' ? code.target.url : ETIQUETAS_TIPO[code.type];
  const total = dias?.reduce((a, b) => a + b, 0) ?? 0;

  const [copiado, setCopiado] = useState(false);
  const [descargando, setDescargando] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 1800);
    return () => clearTimeout(t);
  }, [copiado]);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(shortLink);
      setCopiado(true);
    } catch {
      // Sin permiso de portapapeles (o sin https) no se puede copiar; el
      // enlace sigue a la vista para seleccionarlo a mano.
    }
  }

  // Solo `layout`, sin animacion de entrada.
  //
  // Las fichas SON el contenido de la pantalla, asi que no pueden nacer
  // invisibles esperando a que un fotograma las traiga: en una pestana en
  // segundo plano el navegador congela la animacion y la lista se queda
  // lavada a medio camino. Comprobado en el navegador, no supuesto.
  //
  // Donde motion si aporta es al reordenar: al cambiar el criterio de orden
  // las fichas se deslizan a su sitio nuevo en vez de teletransportarse, y eso
  // cuenta algo real, que es que se han movido.
  return (
    <motion.article layout transition={{ duration: 0.18 }} className={FICHA}>
      <div className="flex items-start gap-3 2xl:gap-4">
        {/* El QR se dibuja una sola vez a 160 px y el SVG se estira a su hueco:
            es vectorial, asi que no pierde nitidez por mucho que crezca. */}
        <div className={FICHA_QR}>
          <QrPreview
            value={shortLink}
            size={160}
            showDownloads={false}
            foreground={code.design?.foreground}
            background={code.design?.background}
            moduleShape={code.design?.moduleShape}
            eyeShape={code.design?.eyeShape}
            logoUrl={logoDe(code.design)}
          />
        </div>

        <div className="min-w-0 flex-1">
          <Link
            to={`/codigos/${code.id}`}
            className={FICHA_TITULO}
            title={code.name}
          >
            {code.name}
          </Link>
          <span className={FICHA_ETIQUETA}>{ETIQUETAS_TIPO[code.type]}</span>
        </div>
      </div>

      <div className={FICHA_ENLACE}>
        <code className="min-w-0 flex-1 truncate font-mono">
          {shortLink.replace(/^https?:\/\//, '')}
        </code>
      </div>

      <div className="flex items-end justify-between gap-2.5">
        {/* La cifra grande es el total de siempre, no la de la semana: un "0"
            enorme en un codigo que lleva nueve escaneos hace dudar de si el
            panel esta roto. La tendencia la cuenta la chispa de al lado. */}
        <span>
          <b className={FICHA_CIFRA}>{code.scanCount.toLocaleString('es')}</b>
          <span className={FICHA_PIE}>
            {code.scanCount === 1 ? 'escaneo' : 'escaneos'} en total
          </span>
        </span>

        <span className="flex flex-col items-end gap-0.5">
          <Chispa valores={dias} className={FICHA_CHISPA} />
          <span className={FICHA_PIE}>
            {total > 0 ? `${total} esta semana` : 'nada esta semana'}
          </span>
        </span>
      </div>

      <div className="flex items-center justify-between gap-2.5">
        <Estado code={code} />
        <span className={`min-w-0 truncate ${FICHA_PIE}`} title={destino}>
          {destino.replace(/^https?:\/\//, '')}
        </span>
      </div>

      <div className="flex gap-1.5 border-t border-line pt-3 2xl:gap-2 2xl:pt-4">
        <button type="button" className={BOTON_FICHA} onClick={copiar}>
          {copiado ? 'Copiado' : 'Copiar'}
        </button>
        <button
          type="button"
          className={BOTON_FICHA}
          disabled={descargando}
          onClick={async () => {
            setDescargando(true);
            try {
              // PNG y no SVG desde la lista: quien descarga de aqui suele
              // querer pegarlo en un mensaje o en un documento. El SVG para
              // imprenta esta en el detalle, junto al resto del diseno.
              await descargarQr(
                {
                  value: shortLink,
                  foreground: code.design?.foreground,
                  background: code.design?.background,
                  moduleShape: code.design?.moduleShape,
                  eyeShape: code.design?.eyeShape,
                  logoUrl: logoDe(code.design),
                },
                code.slug,
                'png',
              );
            } finally {
              setDescargando(false);
            }
          }}
        >
          {descargando ? 'Generando…' : 'Descargar'}
        </button>
        <Link to={`/codigos/${code.id}`} className={`${BOTON_FICHA} no-underline`}>
          Editar
        </Link>
      </div>
    </motion.article>
  );
}

function Estado({ code }: { readonly code: CodeRow }) {
  if (code.safety === 'flagged') {
    return <span className="badge badge--danger">Bloqueado</span>;
  }
  if (!code.active) return <span className="badge badge--muted">Pausado</span>;
  if (code.safety === 'pending') {
    return <span className="badge badge--warning">Comprobando</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-2">
      <span className="size-1.5 shrink-0 rounded-full bg-ok" />
      Activo
    </span>
  );
}

function Vacio({
  puedeCrear,
  onCrear,
}: {
  readonly puedeCrear: boolean;
  readonly onCrear: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-line px-6 py-14 text-center">
      <h2 className="m-0 text-base font-semibold">Aún no hay ningún código</h2>
      <p className="m-0 max-w-sm text-ink-2">
        Crea el primero y llévalo a imprenta. Podrás cambiar a dónde lleva cuántas
        veces quieras sin volver a imprimir nada.
      </p>
      {puedeCrear ? (
        <Button onClick={onCrear} className="mt-1">
          Crear el primer código
        </Button>
      ) : null}
    </div>
  );
}

function NewCodeForm({
  orgId,
  onHecho,
}: {
  readonly orgId: string | null;
  readonly onHecho: () => void;
}) {
  const [type, setType] = useState<QrType>('url');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [payload, setPayload] = useState<PayloadValue>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<
    { slug: string; name: string; shortHost: string } | null
  >(null);

  const esEnlace = type === 'url';

  function cambiarTipo(nuevo: QrType) {
    setType(nuevo);
    // El contenido de cada tipo no tiene nada que ver con el de otro, así que
    // se reinicia en vez de arrastrar campos que ya no significan nada.
    setPayload(payloadInicial(nuevo));
    setError(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!orgId) return;

    setError(null);
    setBusy(true);
    try {
      const result = await api.createQrCode({
        orgId,
        name: name.trim(),
        type,
        ...(esEnlace ? { targetUrl: url.trim() } : { payload }),
      });
      setCreated({
        slug: result.slug,
        name: name.trim(),
        shortHost: result.shortHost,
      });
      setName('');
      setUrl('');
      setPayload(payloadInicial(type));
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  const puedeGuardar =
    name.trim() !== '' && (esEnlace ? url.trim() !== '' : payloadListo(type, payload));

  if (created) {
    const shortLink = shortLinkOf(created, SHORT_LINK_BASE);
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="rounded-lg bg-white p-3">
          <QrPreview value={shortLink} size={180} fileName={created.slug} />
        </div>

        <div>
          <p className="m-0 text-[12.5px] text-ink-3">Enlace corto</p>
          <p className="m-0 font-mono text-[13.5px]">{shortLink}</p>
        </div>

        <p className="m-0 max-w-xs text-[13px] text-ink-2">
          Descarga el SVG para imprenta: mantiene la nitidez a cualquier tamaño, desde
          una pegatina hasta una valla.
        </p>

        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setCreated(null)}>
            Crear otro
          </Button>
          <Button onClick={onHecho}>Hecho</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="stack-sm">
      {/* El tipo va primero porque cambia todo lo que viene debajo. */}
      <div className="types" role="radiogroup" aria-label="Tipo de código">
        <button
          type="button"
          role="radio"
          aria-checked={esEnlace}
          className={`types__item${esEnlace ? ' types__item--active' : ''}`}
          onClick={() => cambiarTipo('url')}
        >
          <strong>Enlace</strong>
          <span>Lleva a una página web.</span>
        </button>

        {HOSTED_TYPES.map((t) => (
          <button
            key={t.type}
            type="button"
            role="radio"
            aria-checked={type === t.type}
            className={`types__item${type === t.type ? ' types__item--active' : ''}`}
            onClick={() => cambiarTipo(t.type)}
          >
            <strong>{t.label}</strong>
            <span>{t.hint}</span>
          </button>
        ))}
      </div>

      <Field
        label="Nombre"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Cartel escaparate"
        maxLength={120}
        required
        hint="Para reconocerlo en la lista. No aparece al escanear."
      />

      {esEnlace ? (
        <Field
          label="Destino"
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://tu-web.com/promoción"
          required
          hint="A dónde llega quien escanea. Podrás cambiarlo más adelante."
          error={error}
        />
      ) : (
        <>
          <PayloadForm
            type={type}
            value={payload}
            orgId={orgId}
            onChange={setPayload}
          />
          {error ? <Callout tone="danger">{error}</Callout> : null}
        </>
      )}

      <div>
        <Button type="submit" loading={busy} disabled={!puedeGuardar}>
          Crear código
        </Button>
      </div>
    </form>
  );
}

/** Las claves de los últimos días, del más antiguo al de hoy. */
function diasRecientes(): string[] {
  const hoy = new Date();
  return Array.from({ length: DIAS_CHISPA }, (_, i) => {
    const d = new Date(hoy);
    d.setUTCDate(d.getUTCDate() - (DIAS_CHISPA - 1 - i));
    return dayKey(d);
  });
}
